import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import "./Gallery.css";

import { useApiQuery, resolveFileUrl } from "../api/client";

const Gallery = () => {
  const { ref, inView } = useInView({ threshold: 0.18, triggerOnce: true });
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const { data, isLoading, isError } = useApiQuery("/gallery");
  const galleryItems = data || [];

  const images = useMemo(
    () => galleryItems.map((g) => resolveFileUrl(g.imageUrl)),
    [galleryItems],
  );

  const slides = images.map((src) => ({ src }));

  const handleOpen = (i) => {
    setIndex(i);
    setOpen(true);
  };

  return (
    <section className="gal" id="gallery" ref={ref}>
      <div className="gal__container">
        <motion.div
          className="gal__head"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <div className="gal__kicker">GALLERY</div>
          <h2 className="gal__title">A look inside the experience.</h2>
          <p className="gal__sub">
            Premium finishes, modern spaces, and details you can feel the moment
            you walk in.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.08 }}
        >
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            spaceBetween={14}
            slidesPerView={3}
            breakpoints={{
              0: { slidesPerView: 1.08 },
              520: { slidesPerView: 1.6 },
              820: { slidesPerView: 2.4 },
              1100: { slidesPerView: 3 },
            }}
            className="gal__swiper"
          >
            {isLoading && (
              <SwiperSlide>
                <div className="gal__imgBtn" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  Loading gallery…
                </div>
              </SwiperSlide>
            )}
            {isError && (
              <SwiperSlide>
                <div className="gal__imgBtn" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  Couldn't load gallery right now.
                </div>
              </SwiperSlide>
            )}
            {!isLoading && !isError && images.length === 0 && (
              <SwiperSlide>
                <div className="gal__imgBtn" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  No photos posted yet.
                </div>
              </SwiperSlide>
            )}
            {images.map((src, idx) => (
              <SwiperSlide key={idx}>
                <button
                  className="gal__imgBtn"
                  onClick={() => handleOpen(idx)}
                  type="button"
                >
                  <img
                    className="gal__img"
                    src={src}
                    alt={`Gallery ${idx + 1}`}
                    loading="lazy"
                  />
                  <div className="gal__imgOverlay" />
                  <div className="gal__imgHint">View</div>
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      </div>

      {/* Lightbox */}
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={slides}
        index={index}
      />
    </section>
  );
};

export default Gallery;
